/**
 * components/team/PermissionMatrix.js
 * One permission matrix for every team screen. Pass `readOnly` to render a
 * role's defaults as a job description instead of an editor, and `lockedKeys`
 * for permissions every role carries (shown ticked, cannot be unticked).
 */
import React, { useMemo } from "react";
import { S, DOMAIN_LABELS } from "./teamStyles.js";

/**
 * The permission matrix. Grouped by domain so a role reads as a job
 * description rather than an alphabetical key dump. RESERVED keys are shown
 * but visibly marked, because granting one today does nothing.
 */
const PermissionMatrix = ({ permissions, selected, onToggle, readOnly, lockedKeys }) => {
  const locked = useMemo(() => new Set(lockedKeys || []), [lockedKeys]);
  const byDomain = useMemo(() => {
    const groups = new Map();
    for (const p of permissions) {
      if (!groups.has(p.domain)) groups.set(p.domain, []);
      groups.get(p.domain).push(p);
    }
    return [...groups.entries()];
  }, [permissions]);

  return (
    <div>
      {byDomain.map(([domain, perms]) => (
        <div key={domain} style={S.matrixDomain}>
          <div style={S.matrixDomainTitle}>{DOMAIN_LABELS[domain] || domain}</div>
          <div style={S.matrixGrid}>
            {perms.map((p) => {
              const isLocked = locked.has(p.key);
              const checked = isLocked || selected.has(p.key);
              const disabled = readOnly || isLocked;
              return (
                <label key={p.key} style={S.matrixCell(checked, disabled && !readOnly)} title={p.description}>
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={disabled}
                    onChange={() => onToggle && !disabled && onToggle(p.key)}
                    style={{ marginTop: 2, accentColor: "#083860" }}
                  />
                  <span>
                    <span style={{ fontWeight: 600, color: "#1e293b" }}>{p.label}</span>
                    {p.status === "RESERVED" ? (
                      <span style={{ ...S.pill("#b45309", "#fef3c7"), marginLeft: 6 }}>RESERVED</span>
                    ) : null}
                    {isLocked && !readOnly ? (
                      <span style={{ ...S.pill("#475569", "#f1f5f9"), marginLeft: 6 }}>Every role</span>
                    ) : null}
                    <span style={{ display: "block", color: "#64748b", fontSize: 11, marginTop: 2, lineHeight: 1.4 }}>{p.description}</span>
                  </span>
                </label>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

export default PermissionMatrix;
